// =============================================================================
// VulnTracker — Stat Card Component
// =============================================================================
import { useState } from 'react';
import clsx from 'clsx';

interface Props {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  info?: string;
  trend?: { value: number; direction: 'up' | 'down' };
  className?: string;
  valueClassName?: string;
}

export function StatCard({ title, value, icon, info, trend, className, valueClassName }: Props) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className={clsx('card relative', className)}>
      {/* Info icon */}
      {info && (
        <div className="absolute top-2 right-2">
          <button
            type="button"
            className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-200 text-gray-500 text-xs font-bold hover:bg-primary-100 hover:text-primary-700 focus:outline-none transition-colors"
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo((prev) => !prev)}
            aria-label={`Info about ${title}`}
          >
            i
          </button>
          {showInfo && (
            <div className="absolute right-0 top-7 z-50 w-64 rounded-lg bg-gray-900 text-white text-xs leading-relaxed p-3 shadow-lg">
              <div className="absolute -top-1 right-2 h-2 w-2 rotate-45 bg-gray-900" />
              {info}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className={clsx('mt-1 text-3xl font-bold tracking-tight', valueClassName || 'text-gray-900')}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
        </div>
        {icon && (
          <div className="flex-shrink-0 rounded-lg bg-primary-50 p-3">
            {icon}
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-3 flex items-center text-sm">
          <span
            className={clsx(
              'font-medium',
              trend.direction === 'up' ? 'text-red-600' : 'text-green-600',
            )}
          >
            {trend.direction === 'up' ? '↑' : '↓'} {Math.abs(trend.value)}%
          </span>
          <span className="ml-2 text-gray-500">vs last period</span>
        </div>
      )}
    </div>
  );
}
